import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertCircle, Award, BookOpen, Check, Loader2, Route, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import { useQuery } from '@tanstack/react-query';
import { businessService } from '@/services/business.service';
import { ltiService } from '@/services/lti.service';
import {
  LTI_CONTENT_LABEL,
  loadLtiContent,
  type LtiContentItem,
} from '@/services/lti-content.catalogue';
import { LtiResourceType, type LtiSelectableResource } from '@/types/lti.types';

interface Tab {
  type: LtiResourceType;
  label: string;
  icon: typeof BookOpen;
}

const PAGE_SIZE = 50;

const TABS: Tab[] = [
  { type: LtiResourceType.COURSE, label: 'Courses', icon: BookOpen },
  { type: LtiResourceType.CERTIFICATION, label: 'Certifications', icon: Award },
  { type: LtiResourceType.LEARNING_PATH, label: 'Learning paths', icon: Route },
];

/** LTI Deep Linking content picker. */
export default function LtiContentSelect() {
  const { data: businessData } = useQuery({
    queryKey: ['businessSettings'],
    queryFn: () => businessService.getBusinessSettings(),
    staleTime: 5 * 60 * 1000,
  });
  const companyName = businessData?.data?.company_name?.trim() || businessData?.company_name?.trim() || 'Skillzen';

  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session');

  const [activeTab, setActiveTab] = useState<LtiResourceType>(LtiResourceType.COURSE);
  const [items, setItems] = useState<LtiContentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  /** Debounced copy of `search`, so typing does not fire a request per keystroke. */
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<LtiContentItem | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const submitted = useRef(false);

  const tab = TABS.find(t => t.type === activeTab)!;
  const noun = LTI_CONTENT_LABEL[activeTab];

  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    // Searching server-side matters: a tenant with hundreds of courses would
    // otherwise only ever be able to find one in the first page.
    loadLtiContent(activeTab, { search: query, limit: PAGE_SIZE })
      .then(loaded => {
        if (!cancelled) setItems(loaded);
      })
      .catch(error => {
        console.error(`Failed to load ${noun}s for LTI selection`, error);
        if (!cancelled) {
          setItems([]);
          setLoadError(`Could not load your ${noun}s. Please close this window and try again.`);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [query, activeTab]);

  const switchTab = (type: LtiResourceType) => {
    if (type === activeTab || submitting) return;
    setActiveTab(type);
    setItems([]);
    // A selection belongs to the tab it was made on — carrying it across would
    // let the teacher add something they can no longer see.
    setSelected(null);
  };

  /** Hand the signed response to the LMS. */
  const postToPlatform = (returnUrl: string, jwt: string) => {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = returnUrl;

    const field = document.createElement('input');
    field.type = 'hidden';
    field.name = 'JWT';
    field.value = jwt;
    form.appendChild(field);

    document.body.appendChild(form);
    form.submit();
  };

  const handleAdd = async () => {
    if (!sessionId || !selected || submitted.current) return;

    submitted.current = true;
    setSubmitting(true);

    const resource: LtiSelectableResource = {
      resource_type: activeTab,
      resource_id: selected.id,
      title: selected.title,
      description: selected.description,
      image_url: selected.image_url,
    };

    try {
      const { return_url, jwt } = await ltiService.completeDeepLinking(sessionId, [resource]);
      postToPlatform(return_url, jwt);
      // No state reset: the browser is navigating away to the LMS.
    } catch (error) {
      submitted.current = false;
      setSubmitting(false);
      console.error('Deep linking completion failed', error);
      toast.error(
        `Could not add this ${noun}. The selection may have expired — please start again from your LMS.`,
      );
    }
  };

  if (!sessionId) {
    return (
      <Centered>
        <AlertCircle className="w-10 h-10 text-red-500 mb-3" />
        <h1 className="text-lg font-semibold text-gray-900">Nothing to select</h1>
        <p className="text-gray-500 mt-2 max-w-md">
          {`This page is opened from your LMS when adding ${companyName} content. Please start again from there.`}
        </p>
      </Centered>
    );
  }

  const EmptyIcon = tab.icon;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <header className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">Choose content</h1>
          <p className="text-gray-500 mt-1">
            Pick what to add to your LMS. Learners will open it without signing in again, and their
            grade returns to your gradebook automatically.
          </p>
        </header>

        <div className="flex gap-1 mb-5 border-b border-gray-200" role="tablist">
          {TABS.map(({ type, label, icon: Icon }) => {
            const isActive = type === activeTab;
            return (
              <button
                key={type}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => switchTab(type)}
                disabled={submitting}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition
                  ${isActive
                    ? 'border-primary-600 text-primary-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            );
          })}
        </div>

        <div className="relative mb-5">
          {loading
            ? <Loader2 className="w-4 h-4 text-primary-600 animate-spin absolute left-3 top-1/2 -translate-y-1/2" />
            : <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />}
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={`Search ${noun}s…`}
            className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        {loadError ? (
          <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
            <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
            <p className="text-gray-700 max-w-md mx-auto">{loadError}</p>
          </div>
        ) : loading && items.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
            <Loader2 className="w-10 h-10 text-primary-600 animate-spin mx-auto mb-3" />
            <p className="text-gray-600">Loading your {noun}s…</p>
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
            <EmptyIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600">
              {query
                ? `No published ${noun}s match that search.`
                : `You have no published ${noun}s yet.`}
            </p>
          </div>
        ) : (
          <ul className="space-y-2" role="listbox" aria-label={`Available ${noun}s`}>
            {items.map(item => {
              const isSelected = selected?.id === item.id;
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => setSelected(item)}
                    disabled={submitting}
                    className={`w-full text-left flex items-start gap-3 p-4 rounded-xl border transition
                      ${isSelected
                        ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500'
                        : 'border-gray-200 bg-white hover:border-gray-300'}`}
                  >
                    <span
                      className={`mt-0.5 w-5 h-5 rounded-full border flex items-center justify-center shrink-0
                        ${isSelected ? 'bg-primary-600 border-primary-600' : 'border-gray-300'}`}
                    >
                      {isSelected && <Check className="w-3 h-3 text-white" />}
                    </span>
                    <span className="min-w-0">
                      <span className="block font-medium text-gray-900 truncate">{item.title}</span>
                      {item.description && (
                        <span className="text-sm text-gray-500 line-clamp-2 mt-0.5">
                          {item.description}
                        </span>
                      )}
                      {item.meta && (
                        <span className="block text-xs text-gray-400 mt-1">{item.meta}</span>
                      )}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <div className="sticky bottom-0 mt-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
          <button
            type="button"
            onClick={handleAdd}
            disabled={!selected || submitting}
            className="px-5 py-2.5 rounded-lg bg-primary-600 text-white font-medium
                       disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary-700
                       flex items-center gap-2"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {submitting ? 'Adding…' : 'Add to my LMS'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 text-center px-4">
      {children}
    </div>
  );
}
